import { Challenge, makeWidgetsList } from '@libs/core';
import { useOnline } from '@libs/hooks';
import { isShowChallenge, selectRootWidgetUids, useAppSelector } from '@libs/store';
import { CoreProvider } from '@libs/theme';
import { SystemEventTypeEnum } from '@libs/types';
import { ConditionalRender, eventBus, getLocale } from '@libs/utils';
import React, { useLayoutEffect, useEffect } from 'react';
import { Offline } from './common/Offline';
import { PortalWrapper } from './common/PortalWrapper';
import { ConsumerThemeProvider } from './theme/ConsumerThemeProvider';
import './theme/general.css';
import Widget from './Widget';
import { WidgetWrapper } from './WidgetWrapper';

const WidgetsList = makeWidgetsList(Widget);

export function App() {
    const rootWidgetUids = useAppSelector(selectRootWidgetUids);
    const statusShowChallenge = useAppSelector(isShowChallenge);
    const isOnline = useOnline();

    // useLayoutEffect (not useEffect) on purpose: it runs synchronously before the first paint,
    // so the `lang` attribute is set on <html> before widgets render. This ensures the `:lang(...)`
    // CSS selectors that switch font-family (GT Standard / GT America / Noto Sans SC) match on the
    // first frame and avoids a visible font flash and re-layout once widgets are already mounted.
    useLayoutEffect(() => {
        const locale = getLocale();
        if (locale) {
            document.documentElement.lang = locale;
        }
    }, []);

    useEffect(() => {
        let documentScrolled = false;
        const handleScroll = () => {
            const scrolled = window.scrollY !== 0;
            if (documentScrolled !== scrolled) {
                documentScrolled = scrolled;
                eventBus.emitGeneral(SystemEventTypeEnum.SCROLLED, scrolled);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <CoreProvider>
            <ConsumerThemeProvider>
                <Offline />
                <Challenge />
                <ConditionalRender condition={isOnline && !statusShowChallenge}>
                    <PortalWrapper>
                        <WidgetWrapper>
                            <WidgetsList uids={rootWidgetUids} />
                        </WidgetWrapper>
                    </PortalWrapper>
                </ConditionalRender>
            </ConsumerThemeProvider>
        </CoreProvider>
    );
}
